import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { IFeedSite } from "../../classes/FeedSite";
import { IConfig } from "../../Config";
import DataService from "../../data/DataService";
import { AlertDialog, ConfirmDialog } from "../Dialogs";
import Loader from "../Loader";
import FeedEditModal from "./FeedEditModal";

interface IProps {
    config: IConfig;
    onClose: () => void;
}

interface IState {
    alertMessage: string;
    allFeedNames: Array<{feedId: string, feedName: string}>;
    editFeedNameDisabled: boolean;

    feedName: string;
    feeds: any[];
    isAddOpen: boolean;
    isConfirmDeleteOpen: boolean;
    isEditOpen: boolean;
    isLoading: boolean;
    selectedFeedId: string;
    selectedServerId: number;
    selectedServerIds: string[];
    sites: IFeedSite[];
    skip: number;
    take: number;
    totalFeeds: number;
}

const FeedsPerPage = 10;

export default class ManageFeeds extends React.Component<IProps, IState> {
    private dataService: DataService;

    public constructor(props) {
        super(props);
        this.state = {
            alertMessage: "",
            allFeedNames: [],
            editFeedNameDisabled: false,
            feedName: "",
            feeds: [],
            isAddOpen: false,
            isConfirmDeleteOpen: false,
            isEditOpen: false,
            isLoading: false,
            selectedFeedId: null,
            selectedServerId: null,
            selectedServerIds: [],
            sites: [],
            skip: 0,
            take: FeedsPerPage,
            totalFeeds: 0,
        };
        this.dataService = new DataService(this.props.config);
    }

    public render() {
        return (
            <Loader isLoading={this.state.isLoading}>
                <div className="CMS_element settings">
                    <div className="CMS_title CMS_T4">Manage feeds</div>
                    <div className="CMS_e_row">
                        <div className="CMS_title CMS_T6">Feeds</div>
                        <Grid
                            style={{ height: "400px" }}
                            data={_.map(this.state.feeds,
                                (item) => ({
                                    ...item,
                                    createdDate: DateTime.fromISO(item.created).toLocaleString(DateTime.DATETIME_FULL),
                                    selected: item.feedId === this.state.selectedFeedId,
                                }))
                            }
                            skip={this.state.skip}
                            take={this.state.take}
                            total={this.state.totalFeeds}
                            pageable={true}
                            selectedField="selected"
                            onPageChange={this.onPageChange.bind(this)}
                            onRowClick={(e) => {
                                this.setState({ selectedFeedId: e.dataItem.feedId });
                            }}
                        >
                            <GridToolbar>
                                <button
                                    title="Add feed"
                                    className="k-button k-primary"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.setState({ isAddOpen: true, feedName: "", editFeedNameDisabled: false, selectedFeedId: null });
                                        this.refreshSites();
                                    }}
                                >
                                    Add feed
                                </button>
                                <button
                                    title="Edit feed"
                                    className="k-button k-primary"
                                    disabled={_.isEmpty(this.state.selectedFeedId)}
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        const feed = _.find(this.state.feeds, { feedId: this.state.selectedFeedId });
                                        this.setState({ isEditOpen: true, feedName: feed.feedName });
                                        this.refreshSites(this.state.selectedFeedId);
                                    }}
                                >
                                    Edit feed
                                </button>
                                <button
                                    title="Remove feed"
                                    className="k-button k-primary"
                                    disabled={_.isEmpty(this.state.selectedFeedId)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.setState({ isConfirmDeleteOpen: true });
                                    }}
                                >
                                    Remove feed
                                </button>
                            </GridToolbar>
                            <GridColumn field="feedName" title="Feed Name" />
                            <GridColumn field="createdDate" title="Created At" />
                        </Grid>
                    </div>
                    <div className="CMS_buttonGroup CMS_bg-R">
                        <button
                            className="CMS_button CMS_bCancel"
                            onClick={(e) => {
                                e.preventDefault();
                                this.props.onClose();
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
                <ConfirmDialog
                    isOpen={this.state.isConfirmDeleteOpen}
                    message={`Are you sure you want to delete this feed?`}
                    callBack={async (value) => {
                        if (value) {
                            await this.deleteFeed();
                        }
                        this.setState({ isConfirmDeleteOpen: false });
                    }}
                />
                {(this.state.isAddOpen || this.state.isEditOpen)
                    && <FeedEditModal
                        allFeedNames={this.state.allFeedNames}
                        feedId={this.state.selectedFeedId}
                        feedName={this.state.feedName}
                        onSave={async () => {
                            if (this.state.isAddOpen) {
                                await this.addFeed();
                            } else {
                                await this.editFeed();
                            }
                        }}
                        onCancel={() => {
                            this.setState({ feedName: "", isAddOpen: false, isEditOpen:
                                 false });
                        }}
                        serverId={this.props.config.districtServerId}
                        sites={this.state.sites}
                        title={`${this.state.isAddOpen ? "Add" : "Edit"} feed`}
                        updateFeedName={(newFeedName: string) => {
                            this.setState({ feedName: newFeedName });
                        }}
                        updateSites={(data: IFeedSite[]) => {
                            this.setState({ sites: data });
                        }}
                    />}
                <AlertDialog
                    isOpen={!_.isEmpty(this.state.alertMessage)}
                    message={this.state.alertMessage}
                    callBack={() => this.setState({ alertMessage: "" })}
                />
            </Loader>
        );
    }

    public async componentDidMount() {
        await this.loadFeeds();
    }

    private async loadFeeds(page: number = 1) {
        try {
            this.setState({ isLoading: true });
            const feeds = (await this.dataService.getFeeds(page, FeedsPerPage)).data || {};
            this.setState({ feeds: feeds.items, totalFeeds: feeds.total || 0 });
            const allfeeds = (await this.dataService.getFeeds(1, 1000)).data || {};
            const allNames = this.getAllFeedNames(allfeeds);
            this.setState({ allFeedNames: allNames });
            const allSites = (await this.dataService.getAllSubscribedAndPublishedSites(this.state.selectedFeedId)) || [];
            this.setState({ sites: allSites });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    private getAllFeedNames(feeds): Array<{feedId: string, feedName: string}> {
        if (!feeds?.items) {
            return [];
        }

        return feeds.items.map((f) => ({
                feedId : f.feedId,
                feedName : f.feedName,
        }));
    }

    private onPageChange(e) {
        this.setState({
            skip: e.page.skip,
            take: e.page.take,
        }, async () => await this.loadFeeds(e.page.skip / e.page.take + 1));
    }

    private async addFeed() {
        try {
            this.setState({ isAddOpen: false, isLoading: true });
            const feed = (await this.dataService.addFeed(this.state.feedName)).data;
            this.setState({ feedName: "" });
            this.saveFeedSites(feed.feedId);
            await this.loadFeeds();
        } finally {
            this.setState({ isLoading: false });
        }
    }

    private async refreshSites(selectedId: string = null) {
        const allSites = (await this.dataService.getAllSubscribedAndPublishedSites(selectedId)) || [];
        this.setState({ sites: allSites });
    }

    private async saveFeedSites( selectedFeedId: string ) {
        const data = [];

        this.state.sites.map((item) => {
            if (item.isSubscribed === true) {
                const site = {feedId : selectedFeedId, serverId : item.serverId, isPublished : item.isPublished};

                data.push(site);
            }
        });

        await this.dataService.postFeedWebsites(selectedFeedId, data);
    }

    private async editFeed() {
        try {
            this.setState({ isEditOpen: false, isLoading: true });
            const feed = (await this.dataService.getFeed(this.state.selectedFeedId)).data;
            feed.feedName = this.state.feedName;
            await this.dataService.updateFeed(this.state.selectedFeedId, feed);
            this.setState({ feedName: "" });
            this.saveFeedSites(this.state.selectedFeedId);
            await this.loadFeeds();
        } finally {
            this.setState({ isLoading: false });
        }
    }

    private async deleteFeed() {
        try {
            this.setState({ isLoading: true });
            const selectedFeedsIds = [];

            selectedFeedsIds.push(this.state.selectedFeedId);

            const news = (await this.dataService.getAllFeedsNews(selectedFeedsIds, 1, 1, false)).data;
            if (news?.total > 0) {
                this.setState({ isLoading: false, alertMessage: "Cannot delete a feed that has active posts" });
                return;
            }
            await this.dataService.deleteFeed(this.state.selectedFeedId);
            this.setState({ selectedFeedId: null });
            await this.loadFeeds();
        } finally {
            this.setState({ isLoading: false });
        }
    }
};
